/**
 * ChangePasswordPage — mandatory password change for accounts flagged with
 * `must_change_password: true` (teacher-invite onboarding, and the Stripe
 * provisional-password flow).
 *
 * Reached automatically via the guard in App.js: when the user's flag is true,
 * every ProtectedRoute redirects here and no other route renders until the
 * flag is cleared by the backend. This is a defense-in-depth layer — AuthPage
 * already shows an inline change-password form right after login, but that
 * only covers the synchronous case; this route-level guard also protects
 * against a page refresh or a direct URL visit mid-flow.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { validatePassword, PasswordRequirements } from '../utils/validatePassword';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LockKey, Eye, EyeSlash, Spinner } from '@phosphor-icons/react';

export default function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const forced = !!user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pwdErr = validatePassword(newPw);
    if (pwdErr) { toast.error(pwdErr); return; }
    if (newPw !== confirmPw) { toast.error('Passwörter stimmen nicht überein'); return; }
    if (newPw === currentPw) { toast.error('Das neue Passwort muss sich vom alten unterscheiden'); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      await changePassword({ current_password: currentPw, new_password: newPw });
      toast.success('Passwort erfolgreich geändert!');
      // Land the user on their natural home — teachers → teacher dashboard,
      // everyone else → student dashboard.
      navigate(user?.role === 'admin' || user?.role === 'teacher' ? '/teacher' : '/dashboard', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Passwort-Änderung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen canvas-spain flex items-center justify-center px-4 py-8" data-testid="change-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#AB1C24] mb-4 shadow-lg shadow-[#AB1C24]/20">
            <LockKey size={28} weight="fill" className="text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#292524]">
            {forced ? 'Passwort festlegen' : 'Passwort ändern'}
          </h1>
          <p className="text-sm text-[#78716C] mt-2">
            {forced
              ? 'Willkommen! Bevor du Habla nutzen kannst, wähle bitte ein eigenes Passwort.'
              : 'Wähle ein neues Passwort für dein Konto.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4 shadow-sm">
          <div>
            <Label htmlFor="cp-current" className="text-xs">
              {forced ? 'Temporäres Passwort (aus der E-Mail)' : 'Aktuelles Passwort'} *
            </Label>
            <div className="relative">
              <Input
                id="cp-current"
                type={showCurrent ? 'text' : 'password'}
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="cp-current-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#78716C] hover:text-[#292524]"
                tabIndex={-1}
                aria-label="Passwort anzeigen"
              >
                {showCurrent ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="cp-new" className="text-xs">Neues Passwort *</Label>
            <div className="relative">
              <Input
                id="cp-new"
                type={showNew ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="cp-new-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#78716C] hover:text-[#292524]"
                tabIndex={-1}
                aria-label="Passwort anzeigen"
              >
                {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <PasswordRequirements password={newPw} />
          </div>

          <div>
            <Label htmlFor="cp-confirm" className="text-xs">Neues Passwort bestätigen *</Label>
            <Input
              id="cp-confirm"
              type={showNew ? 'text' : 'password'}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="cp-confirm-input"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#AB1C24] hover:bg-[#8A151C] text-white h-11 text-base font-semibold"
            data-testid="cp-submit"
          >
            {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : null}
            Passwort speichern
          </Button>

          {forced && (
            <button
              type="button"
              onClick={logout}
              className="w-full text-xs text-[#78716C] hover:text-[#AB1C24] mt-2"
              data-testid="cp-logout-link"
            >
              Doch nicht? Abmelden
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
